import { Sites } from '@ps/platform-message-framework/common/Sites';
import { Currency, FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import useRequest from '@swf/core/hooks/useRequest';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardLoading from '@swf/core/hooks/useWizardLoading';
import { select } from '@swf/core/store';

import type { Limits } from '../../types/responsibleGamingLimits';
import { AccountErrorCode } from '../../types/status';
import Alert from '../Alert/Alert';
import ButtonGroup from '../ButtonGroup/ButtonGroup';
import CTAButton from '../CTAButton/CTAButton';

import {
  TXTWCL_Continue_without_deposit_limit_ELL,
  TXTWCL_Continue_without_loss_limits,
  TXTWCL_You_are_required_to_set_a_depo_ELL,
  TXTWCL_You_need_to_set_at_least_1_lim_ELL
} from './ResponsibleGamingLimits.messages.json';
import { alert, fieldsWrapper, noSwitch } from './ResponsibleGamingLimits.scss';
import DailyLimit from './components/DailyLimit/DailyLimit';
import MonthlyLimit from './components/MonthlyLimit/MonthlyLimit';
import Switch from './components/Switch/Switch';
import WeeklyLimit from './components/WeeklyLimit/WeeklyLimit';

interface Props {
  fieldName: string;
  limits: Limits;
  limitsDisabled?: boolean;
  showConversionInfo?: boolean;
}

const ResponsibleGamingLimits: React.FC<Props> = ({
  fieldName,
  limits,
  limitsDisabled,
  ...props
}) => {
  const { error: wizardError } = useWizardContext();
  const { globalErrors: [globalError] = [] } = wizardError || {};
  const { loading: isServiceLoading } = useWizardLoading();

  const siteId = useSelector(select.cms.getSiteId);
  const request = useRequest();
  const form = useForm();

  const isSpain = siteId === Sites.SPA;
  const isDenmark = siteId === Sites.DEN;

  const allowSkipLimits = useFeature(FeatureFlag.SwcRmfNoLimits);
  const weeklyOnlyMode = useFeature(FeatureFlag.SwcRmfShowOnlyWeeklyLimit);
  const showConversionInfo = useFeature(FeatureFlag.SwcDepositLimitCurrencyConversionInfo);

  const systemCurrency = useSelector(select.account.getLimitCurrency);
  const userCurrency =
    showConversionInfo && siteId === Sites.UK
      ? Currency.BritishPound
      : ((useSelector(select.account.getDefaultCurrencyForCountry) || systemCurrency) as Currency);

  const isMultiCurrency = systemCurrency !== userCurrency;
  const showCurrencyToggle = isMultiCurrency && !showConversionInfo;

  const { daily, weekly, monthly } = limits;

  const hasBackendLimit = useMemo(
    () =>
      [daily, weekly, monthly].some(
        (block) => block && block.limit !== undefined && block.limit !== -1
      ),
    [daily, weekly, monthly]
  );

  const [showNoLimitError, setShowNoLimitError] = useState(false);

  const {
    input: { value: formDailyValue }
  } = useField(`${fieldName}.daily.limit`, { subscription: { value: true } });

  const {
    input: { value: formWeeklyValue }
  } = useField(`${fieldName}.weekly.limit`, { subscription: { value: true } });

  const {
    input: { value: formMonthlyValue }
  } = useField(`${fieldName}.monthly.limit`, { subscription: { value: true } });

  const userHasEnteredAnyValue =
    (formDailyValue ?? '') !== '' ||
    (formWeeklyValue ?? '') !== '' ||
    (formMonthlyValue ?? '') !== '';

  useField(`${fieldName}.rate`, { initialValue: null });

  const {
    input: { value: selectedCurrency }
  } = useField(`${fieldName}.currency`, {
    subscription: { value: true },
    initialValue: userCurrency
  });

  useEffect(() => {
    request({
      service: 'wc3.currency.get.zmFxRates',
      body: { fromCurrency: userCurrency, toCurrency: systemCurrency }
    }).subscribe(({ body }) => {
      const [{ rate }] = body.rates;
      form.change(`${fieldName}.rate`, rate);
    });
  }, [request, userCurrency, systemCurrency, fieldName, form]);

  const handleSubmit = useCallback(() => {
    if (!hasBackendLimit && !userHasEnteredAnyValue) {
      setShowNoLimitError(true);
      return;
    }
    form.change(`${fieldName}.skip`, false);
    form.change(`userLimitsSelect.skipped`, false);
    form.submit();
  }, [hasBackendLimit, userHasEnteredAnyValue, fieldName, form]);

  const handleSkip = useCallback(() => {
    form.change(`${fieldName}.skip`, true);
    form.change(`userLimitsSelect.skipped`, false);
    form.submit();
  }, [fieldName, form]);

  return (
    <>
      {showCurrencyToggle && <Switch currency={userCurrency} fieldName={fieldName} />}
      {showNoLimitError && (
        <Alert variant="error" className={alert}>
          <Translation
            {...(!weeklyOnlyMode
              ? TXTWCL_You_need_to_set_at_least_1_lim_ELL
              : TXTWCL_You_are_required_to_set_a_depo_ELL)}
          />
        </Alert>
      )}
      <div
        className={classNames(fieldsWrapper, { [noSwitch]: !isMultiCurrency && !showNoLimitError })}
      >
        {!weeklyOnlyMode && (
          <DailyLimit
            limits={limits}
            limitsDisabled={limitsDisabled || (daily && daily.limit !== -1)}
            fieldName={fieldName}
            currency={selectedCurrency}
            withPlaceholder
            {...props}
          />
        )}
        <WeeklyLimit
          limits={limits}
          limitsDisabled={limitsDisabled || (weekly && weekly.limit !== -1)}
          fieldName={fieldName}
          currency={selectedCurrency}
          withPlaceholder
          {...props}
        />
        {!weeklyOnlyMode && (
          <MonthlyLimit
            limits={limits}
            limitsDisabled={limitsDisabled || (monthly && monthly.limit !== -1)}
            fieldName={fieldName}
            currency={selectedCurrency}
            withPlaceholder
            {...props}
          />
        )}
        <ButtonGroup>
          <CTAButton
            type="button"
            onClick={handleSubmit}
            disabled={isSpain && isEmpty(limits)}
            {...(isDenmark && {
              loading:
                isServiceLoading || globalError?.code === AccountErrorCode.MIT_ID_AUTH_REQUIRED
            })}
          />
          {allowSkipLimits && (
            <CTAButton
              type="button"
              variant="text"
              onClick={handleSkip}
              translation={
                fieldName === 'rmf.deposit'
                  ? TXTWCL_Continue_without_deposit_limit_ELL
                  : TXTWCL_Continue_without_loss_limits
              }
            />
          )}
        </ButtonGroup>
      </div>
    </>
  );
};

export default ResponsibleGamingLimits;
